import { createContext, useState, useEffect, ReactNode } from 'react';
import jwt_decode, { JwtPayload } from "jwt-decode";
import { useNavigate } from 'react-router-dom';
import { ToastError } from '../constants/Toast';
import AppURL from '../constants/AppURL';

interface AuthContextProps {
  user: JwtPayload | null;
  authTokens: AuthTokens | null;
  loginUser: (e: React.FormEvent<HTMLFormElement>) => void;
  logoutUser: () => void;
  isAuthenticated: boolean;
}

interface AuthTokens {
  access: string;
  refresh: string;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);
export default AuthContext;

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<JwtPayload | null>(null);
  const [authTokens, setAuthTokens] = useState<AuthTokens | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const isTokenExpired = (token: string) => {
    const decodedToken = jwt_decode<JwtPayload>(token);
    return decodedToken.exp && decodedToken.exp * 1000 < Date.now();
  };
  const tokens = localStorage.getItem('authTokens');

  useEffect(() => {
    if (tokens) {
      const parsedTokens = JSON.parse(tokens);
      if (isTokenExpired(parsedTokens.access)) {
        logoutUser();
        setIsAuthenticated(false);
      } else {
        setAuthTokens(parsedTokens);
        setIsAuthenticated(true);
        setUser(jwt_decode<JwtPayload>(parsedTokens.access));
      }
    }
    setLoading(false);
  }, [isAuthenticated, tokens]);

  const loginUser = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { username, password } = e.target as HTMLFormElement;
    if (!username.value || !password.value) {
      ToastError({ message: "Username and Password are required." });
      return;
    }
    const failedAttempts = parseInt(sessionStorage.getItem('failedAttempts') || '0', 10);
    const lastFailedAttempt = parseInt(sessionStorage.getItem('lastFailedAttempt') || '0', 10);
    if (failedAttempts >= 3) {
      const lockoutEnd = lastFailedAttempt + 300000; // 5 minutes lockout time
      const remainingTime = lockoutEnd - Date.now();

      if (remainingTime > 0) {
        const minutes = Math.floor(remainingTime / 60000);
        const seconds = Math.floor((remainingTime % 60000) / 1000);
        ToastError({ message: `Account locked. Please try again in ${minutes} minutes and ${seconds} seconds.` });
        return;
      } else {
        sessionStorage.removeItem('failedAttempts');
        sessionStorage.removeItem('lastFailedAttempt');
      }
    }
    try {
      const response = await fetch(`${AppURL.ApplicationURL}api/token/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.value, password: password.value }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        sessionStorage.setItem('failedAttempts', (failedAttempts + 1).toString());
        sessionStorage.setItem('lastFailedAttempt', Date.now().toString());
        ToastError({ message: errorData.detail || "Something went wrong while logging in the user!" });
        return;
      }
      const data = await response.json();
      sessionStorage.removeItem('failedAttempts');
      sessionStorage.removeItem('lastFailedAttempt');
      sessionStorage.setItem("fullName", data?.fullName);
      localStorage.setItem('authTokens', JSON.stringify(data));
      setAuthTokens(data);
      setIsAuthenticated(true);
      setUser(jwt_decode<JwtPayload>(data.access));
      navigate('/dashboard');
    } catch (error) {
      console.error("Login error:", error);
      ToastError({ message: 'An unexpected error occurred.' });
    }
  };

  const logoutUser = () => {
    localStorage.clear();
    setAuthTokens(null);
    setUser(null);
    setIsAuthenticated(false);
    navigate('/');
  };

  const updateToken = async () => {
    if (!authTokens) return;
    const response = await fetch(AppURL.ApplicationURL + 'api/token/refresh/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh: authTokens?.refresh }),
    });

    const data = await response?.json();
    if (response?.status === 200) {
      setAuthTokens(data);
      setUser(jwt_decode<JwtPayload>(data?.access));
      localStorage.setItem('authTokens', JSON.stringify(data));
    } else {
      logoutUser();
    }

    if (loading) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loading && isAuthenticated) {
      updateToken();
    }
    const REFRESH_INTERVAL = 1000 * 60 * 4; // 4 minutes
    const interval = setInterval(() => {
      if (authTokens && isAuthenticated && !isTokenExpired(authTokens.access)) {
        updateToken();
      }
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [authTokens, isAuthenticated, loading]);

  if (loading) {
    return <div>Loading...</div>;
  }
  const contextData: AuthContextProps = {
    user,
    authTokens,
    loginUser,
    logoutUser,
    isAuthenticated,
  };
  return (
    <AuthContext.Provider value={contextData}>
      {children}
    </AuthContext.Provider>
  );
};
